/* eslint-disable unicorn/no-null */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions, ColumnProps, ColumnSortEvent } from 'primereact/column';
import PrimeReact, { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { useToggleableColumns } from '../../../../hooks/useToggleableColumns';
import { getAccommodationBlockings, getCountries, getDestinations, getRegions } from '../../../../store/actions';
import {
  selectAccommodationBlockings,
  selectAccommodationBlockingsLoading,
  selectAccessToken,
  selectCountries,
  selectDestinations,
  selectRegions,
} from '../../../../store/selectors';
import { IAccommodationBlocking, IIdCodePair } from '../../../../store/actions/sales-configuration';
import { formatDate } from '../../../../utilities/formatting';
import { PAGINATOR_ROWS, PAGINATOR_ROWS_PER_PAGE, PAGINATOR_TEMPLATE } from '../../../constants/paginator-settings';
import {
  isOpenDate,
  OPEN_DATE_TO,
} from '../../../shared/accommodation-blocking-date-range/accommodation-blocking-date-range';
import { dateFilterTemplate } from '../../../shared/filter-templates';
import { GeographyCodesCell } from '../../../shared/geography-codes-cell';
import {
  GEOGRAPHY_MATCH_MODES,
  IGeographyConstraint,
  IGeographyEntry,
  ILookupItem,
  joinGeographyCodes,
  toGeographyEntries,
} from '../../../shared/geography-name-filter';
import styles from './accommodation-blocking-overview.module.scss';

type StatusLabel = 'Pending' | 'Processed';

interface IAccommodationBlockingRow {
  id: number;
  publications: string;
  countries: IGeographyEntry[];
  regions: IGeographyEntry[];
  destinations: IGeographyEntry[];
  accommodations: string;
  airportsOrBusStops: string;
  direction: string;
  dateFrom: Date;
  dateTo: Date;
  type: string;
  durations: string;
  createdBy: string;
  createdDate: string;
  modifiedBy: string;
  modifiedDate: string;
  status: StatusLabel;
  remark: string;
}

function joinCodes(pairs: IIdCodePair[]): string {
  return pairs.map((pair) => pair.code).join(', ');
}

interface IGeographyLookups {
  countries: ILookupItem[];
  regions: ILookupItem[];
  destinations: ILookupItem[];
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function formatDirection(direction: IAccommodationBlocking['transportDirection']): string {
  return direction ?? '';
}

function formatType(type: IAccommodationBlocking['salesSteeringType']): string {
  switch (type) {
    case 'CheckIn': {
      return 'Check In';
    }
    case 'CheckOut': {
      return 'Check Out';
    }
    case 'Stay': {
      return 'Stay';
    }
    default: {
      return '';
    }
  }
}

function toRow(accommodationBlocking: IAccommodationBlocking, lookups: IGeographyLookups): IAccommodationBlockingRow {
  return {
    id: accommodationBlocking.id,
    publications: joinCodes(accommodationBlocking.publications),
    countries: toGeographyEntries(accommodationBlocking.countryCodes, lookups.countries),
    regions: toGeographyEntries(accommodationBlocking.regionCodes, lookups.regions),
    destinations: toGeographyEntries(accommodationBlocking.destinationCodes, lookups.destinations),
    accommodations: joinCodes(accommodationBlocking.accommodations),
    airportsOrBusStops: joinCodes(accommodationBlocking.transportHubs),
    direction: formatDirection(accommodationBlocking.transportDirection),
    dateFrom: new Date(accommodationBlocking.startDate),
    dateTo: new Date(accommodationBlocking.endDate),
    type: formatType(accommodationBlocking.salesSteeringType),
    durations: accommodationBlocking.durations ?? '',
    createdBy: accommodationBlocking.createdBy,
    createdDate: formatDateTime(accommodationBlocking.createdDate),
    modifiedBy: accommodationBlocking.modifiedBy ?? '',
    modifiedDate: formatDateTime(accommodationBlocking.modifiedDate),
    status: accommodationBlocking.isProcessed ? 'Processed' : 'Pending',
    remark: accommodationBlocking.remark,
  };
}

function statusTemplate(row: IAccommodationBlockingRow) {
  const className = row.status === 'Processed' ? styles.statusProcessed : styles.statusPending;
  return <span className={className}>{row.status}</span>;
}

type GeographyField = 'countries' | 'regions' | 'destinations';

function getConstraints(filters: DataTableFilterMeta, field: GeographyField): IGeographyConstraint[] {
  const meta = filters[field];
  return meta && 'constraints' in meta ? meta.constraints : [];
}

function geographyTemplate(field: GeographyField) {
  return function renderGeography(row: IAccommodationBlockingRow, filters: DataTableFilterMeta) {
    return <GeographyCodesCell entries={row[field]} constraints={getConstraints(filters, field)} />;
  };
}

// Sorting stays on the joined code string and mirrors PrimeReact's default text sort (empty values
// always last, numbers inside text compared by value), as when these columns held plain strings.
function sortByJoinedCodes(event: ColumnSortEvent): IAccommodationBlockingRow[] {
  const field = event.field as GeographyField;
  const data = event.data as IAccommodationBlockingRow[];
  const order = event.order ?? 1;
  const collator = new Intl.Collator(PrimeReact.locale, { numeric: true });
  return [...data].sort((a, b) => {
    const first = joinGeographyCodes(a[field]);
    const second = joinGeographyCodes(b[field]);
    if (first === '' || second === '') {
      return first === second ? 0 : first === '' ? 1 : -1;
    }
    return order * collator.compare(first, second);
  });
}

function dateFromTemplate(row: IAccommodationBlockingRow) {
  return formatDate(row.dateFrom);
}

function dateToTemplate(row: IAccommodationBlockingRow) {
  return isOpenDate(row.dateTo) ? 'Open Date' : formatDate(row.dateTo);
}

function DateToFilter({ options }: { options: ColumnFilterElementTemplateOptions }) {
  const [calendarResetKey, setCalendarResetKey] = useState(0);

  const checkboxId = `dateToOpenDateFilter-${options.index}`;
  return (
    <div>
      <React.Fragment key={calendarResetKey}>{dateFilterTemplate(options)}</React.Fragment>
      <div className={styles.dateToOpenDateFilter}>
        <Checkbox
          inputId={checkboxId}
          checked={options.value instanceof Date && isOpenDate(options.value)}
          onChange={(event) => {
            options.filterCallback(event.checked ? new Date(OPEN_DATE_TO) : null, options.index);
            if (!event.checked) {
              // Clearing the value alone leaves PrimeReact's calendar view at December 2099.
              setCalendarResetKey((key) => key + 1);
            }
          }}
        />
        <label htmlFor={checkboxId}>Open Date</label>
      </div>
    </div>
  );
}

function dateToFilterTemplate(options: ColumnFilterElementTemplateOptions) {
  return <DateToFilter options={options} />;
}

interface IToggleableColumn {
  field: keyof IAccommodationBlockingRow;
  header: React.ReactNode;
  label: string;
  defaultHidden?: boolean;
  body?: (row: IAccommodationBlockingRow, filters: DataTableFilterMeta) => React.ReactNode;
  dataType?: ColumnProps['dataType'];
  filterElement?: (options: ColumnFilterElementTemplateOptions) => React.ReactNode;
  filterMatchModeOptions?: ColumnProps['filterMatchModeOptions'];
  sortFunction?: ColumnProps['sortFunction'];
}

const TEXT_MATCH_MODE_OPTIONS = [
  { label: 'Contains', value: FilterMatchMode.CONTAINS },
  { label: 'Starts with', value: FilterMatchMode.STARTS_WITH },
  { label: 'Ends with', value: FilterMatchMode.ENDS_WITH },
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Not contains', value: FilterMatchMode.NOT_CONTAINS },
  { label: 'Not equals', value: FilterMatchMode.NOT_EQUALS },
];

const GEOGRAPHY_MATCH_MODE_OPTIONS = [
  { label: 'Contains', value: GEOGRAPHY_MATCH_MODES.contains },
  { label: 'Starts with', value: GEOGRAPHY_MATCH_MODES.startsWith },
  { label: 'Ends with', value: GEOGRAPHY_MATCH_MODES.endsWith },
  { label: 'Equals', value: GEOGRAPHY_MATCH_MODES.equals },
  { label: 'Not contains', value: GEOGRAPHY_MATCH_MODES.notContains },
  { label: 'Not equals', value: GEOGRAPHY_MATCH_MODES.notEquals },
];

const GEOGRAPHY_COLUMN_PROPS = {
  filterMatchModeOptions: GEOGRAPHY_MATCH_MODE_OPTIONS,
  sortFunction: sortByJoinedCodes,
};

const TOGGLEABLE_COLUMNS: IToggleableColumn[] = [
  { field: 'publications', header: 'Publications', label: 'Publications' },
  {
    field: 'countries',
    header: 'Countries',
    label: 'Countries',
    body: geographyTemplate('countries'),
    ...GEOGRAPHY_COLUMN_PROPS,
  },
  {
    field: 'regions',
    header: 'Regions',
    label: 'Regions',
    body: geographyTemplate('regions'),
    ...GEOGRAPHY_COLUMN_PROPS,
  },
  {
    field: 'destinations',
    header: 'Destinations',
    label: 'Destinations',
    body: geographyTemplate('destinations'),
    ...GEOGRAPHY_COLUMN_PROPS,
  },
  { field: 'accommodations', header: 'Accommodations', label: 'Accommodations' },
  {
    field: 'airportsOrBusStops',
    header: (
      <>
        Airports /<br />
        Bus Stops
      </>
    ),
    label: 'Airports / Bus Stops',
    defaultHidden: true,
  },
  { field: 'direction', header: 'Direction', label: 'Direction', defaultHidden: true },
  {
    field: 'dateFrom',
    header: 'Date From',
    label: 'Date From',
    body: dateFromTemplate,
    dataType: 'date',
    filterElement: dateFilterTemplate,
  },
  {
    field: 'dateTo',
    header: 'Date To',
    label: 'Date To',
    body: dateToTemplate,
    dataType: 'date',
    filterElement: dateToFilterTemplate,
  },
  { field: 'type', header: 'Type', label: 'Type' },
  { field: 'durations', header: 'Durations', label: 'Durations', defaultHidden: true },
  { field: 'createdBy', header: 'Created By', label: 'Created By', defaultHidden: true },
  { field: 'createdDate', header: 'Created Date', label: 'Created Date', defaultHidden: true },
  { field: 'modifiedBy', header: 'Modified By', label: 'Modified By', defaultHidden: true },
  { field: 'modifiedDate', header: 'Modified Date', label: 'Modified Date', defaultHidden: true },
  { field: 'status', header: 'Status', label: 'Status', body: statusTemplate },
  { field: 'remark', header: 'Remark', label: 'Remark', defaultHidden: true },
];

const textFilter = () => ({
  operator: FilterOperator.AND,
  constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
});

// PrimeReact's matchMode type only lists its built-in modes; our registered geography modes are valid at runtime.
const geographyFilter = () => ({
  operator: FilterOperator.AND,
  constraints: [{ value: null, matchMode: GEOGRAPHY_MATCH_MODES.contains as FilterMatchMode }],
});

function isMeaningfulFilterValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  return !Array.isArray(value) || value.length > 0;
}

function hasActiveConstraint(meta: DataTableFilterMeta[string]): boolean {
  if ('constraints' in meta) {
    return meta.constraints.some((constraint) => isMeaningfulFilterValue(constraint.value));
  }
  return isMeaningfulFilterValue(meta.value);
}

export function createInitialFilters(): DataTableFilterMeta {
  return {
    id: textFilter(),
    publications: textFilter(),
    countries: geographyFilter(),
    regions: geographyFilter(),
    destinations: geographyFilter(),
    accommodations: textFilter(),
    airportsOrBusStops: textFilter(),
    direction: textFilter(),
    dateFrom: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    dateTo: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    type: textFilter(),
    durations: textFilter(),
    createdBy: textFilter(),
    createdDate: textFilter(),
    modifiedBy: textFilter(),
    modifiedDate: textFilter(),
    status: textFilter(),
    remark: textFilter(),
  };
}

function AccommodationBlockingOverview() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accommodationBlockings = useSelector(selectAccommodationBlockings);
  const loading = useSelector(selectAccommodationBlockingsLoading);
  const accessToken = useSelector(selectAccessToken);
  const countries = useSelector(selectCountries);
  const regions = useSelector(selectRegions);
  const destinations = useSelector(selectDestinations);
  const [filters, setFilters] = useState<DataTableFilterMeta>(createInitialFilters());
  const [first, setFirst] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATOR_ROWS);
  const [sortField, setSortField] = useState<string | undefined>();
  // PrimeReact does not export its `SortOrder` alias, so mirror it structurally.
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>();
  const [visibleFields, setVisibleFields] = useToggleableColumns(
    'accommodation-blocking-overview.visible-columns',
    TOGGLEABLE_COLUMNS
  );

  useEffect(() => {
    if (accommodationBlockings.length === 0 && accessToken) {
      dispatch(getAccommodationBlockings());
    }
  }, [dispatch, accessToken]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    if (countries.length === 0) {
      dispatch(getCountries());
    }
    if (regions.length === 0) {
      dispatch(getRegions());
    }
    if (destinations.length === 0) {
      dispatch(getDestinations());
    }
  }, [dispatch, accessToken]);

  const rows = useMemo(
    () =>
      accommodationBlockings.map((accommodationBlocking) =>
        toRow(accommodationBlocking, { countries, regions, destinations })
      ),
    [accommodationBlockings, countries, regions, destinations]
  );

  const visibleColumns = useMemo(
    () => TOGGLEABLE_COLUMNS.filter((column) => visibleFields.includes(column.field)),
    [visibleFields]
  );

  const isAnyFilterActive = useMemo(() => Object.values(filters).some((meta) => hasActiveConstraint(meta)), [filters]);

  const clearAllFilters = () => {
    setFilters(createInitialFilters());
    setFirst(0);
  };

  const actionsTemplate = (row: IAccommodationBlockingRow) => (
    <div className={styles.actionsCell}>
      <Button
        icon="pi pi-pencil"
        text
        rounded
        aria-label="Edit"
        onClick={() => navigate(`/accommodation-blocking/edit/${row.id}`)}
      />
      <Button
        icon="pi pi-clone"
        text
        rounded
        aria-label="Copy"
        onClick={() => navigate(`/accommodation-blocking/copy/${row.id}`)}
      />
      <Button
        icon="pi pi-times"
        text
        rounded
        aria-label="Delete"
        onClick={() => navigate(`/accommodation-blocking/delete/${row.id}`)}
      />
    </div>
  );

  return (
    <div className={styles.accommodationBlockingOverview}>
      <div className={styles.header}>
        <div className={styles.headerActions}>
          <MultiSelect
            value={visibleFields}
            options={TOGGLEABLE_COLUMNS}
            optionLabel="label"
            optionValue="field"
            onChange={(event: MultiSelectChangeEvent) => setVisibleFields(event.value)}
            placeholder="Columns"
            display="chip"
            className={styles.columnToggler}
          />
          <Button
            label="Clear All Filters"
            icon="pi pi-filter-slash"
            outlined
            disabled={!isAnyFilterActive}
            onClick={clearAllFilters}
          />
        </div>
        <Button label="New" onClick={() => navigate('/accommodation-blocking/new')} />
      </div>
      <div className={styles.dataTableContainer}>
        <DataTable
          paginator
          first={first}
          rows={rowsPerPage}
          rowsPerPageOptions={PAGINATOR_ROWS_PER_PAGE}
          paginatorTemplate={PAGINATOR_TEMPLATE}
          currentPageReportTemplate="Total: {totalRecords}"
          value={rows}
          loading={loading}
          filterDelay={0}
          // Geography cells highlight from the filter state, which is not part of PrimeReact's cell memo keys.
          cellMemo={false}
          filters={filters}
          onFilter={(event) => {
            setFilters(event.filters);
            setFirst(0);
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={(event) => {
            setSortField(event.sortField);
            setSortOrder(event.sortOrder);
            setFirst(0);
          }}
          onPage={(event) => {
            setFirst(event.first);
            setRowsPerPage(event.rows);
          }}
        >
          <Column field="id" header="Id" sortable filter filterMatchModeOptions={TEXT_MATCH_MODE_OPTIONS}></Column>
          {visibleColumns.map((column) => (
            <Column
              key={column.field}
              field={column.field}
              header={column.header}
              body={column.body && ((row: IAccommodationBlockingRow) => column.body?.(row, filters))}
              dataType={column.dataType}
              filterElement={column.filterElement}
              filterMatchModeOptions={
                column.filterMatchModeOptions ?? (column.dataType === 'date' ? undefined : TEXT_MATCH_MODE_OPTIONS)
              }
              sortFunction={column.sortFunction}
              sortable
              filter
            ></Column>
          ))}
          <Column
            field="actions"
            header="Actions"
            body={(row: IAccommodationBlockingRow) => actionsTemplate(row)}
          ></Column>
        </DataTable>
      </div>
    </div>
  );
}

export default AccommodationBlockingOverview;
